import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { LabelModule } from 'primeng/label';
import type { SidebarSide, SidebarVariant } from 'primeng/types/sidebar';
import { PIcon } from '@primeicons/angular/p-icon';
import { ThemeService } from '../../../theme/services/theme.service';
import type { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import type {
  Company,
  CustomMenuItem,
  NavGroup,
  NavItem,
  NavSubItem,
} from './interfaces/side-bar.interface';
import { COMPANIES, NAV_GROUPS } from './side-bar.data';
// Importación de iconos de PrimeIcons
import { ChevronDown, Moon, Sun, Sidebar, Check } from '@primeicons/angular';
import { AuthService } from '@shared/services/auth.service';
import { AuthorizationService } from '@shared/services/authorization.service';
import type { Permission, PermissionCheckMode } from '@shared/constants/permissions/permissions';
import { CommandMenu } from '../command-menu/command-menu';
import { BrowserStorageService } from '@shared/services/browser-storage.service';
import { ACTIVE_NAVIGATION_CONTEXT_KEY, SUB_MODULE_NAVIGATION_CONTEXT_KEY } from '@shared/constants/navigation-module/navigation-context.constants';
import type { NavigationContext } from '@shared/interfaces/common/navigation-context.interface';

@Component({
  selector: 'app-sidebar',
  imports: [
    RouterLink,
    RouterLinkActive,
    SidebarModule,
    SelectModule,
    AvatarModule,
    PIcon,
    FormsModule,
    SelectButtonModule,
    ToggleSwitchModule,
    ButtonModule,
    LabelModule,
    MenuModule,
    ChevronDown,
    Moon,
    Sun,
    Sidebar,
    Check,
    CommandMenu,
  ],
  templateUrl: './side-bar.html',
  styleUrl: './side-bar.css',
})
export class SideBar {
  public variant: SidebarVariant = 'sidebar';
  public side: SidebarSide = 'left';
  public open: boolean = false;
  public openOnHover: boolean = true;
  public overlay: boolean = false;
  public backdrop: boolean = true;
  public isMobile = signal(false);
  public themeService = inject(ThemeService);
  public authService = inject(AuthService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly browserStorageService = inject(BrowserStorageService);
  private readonly router = inject(Router);
  public readonly user = this.authService.currentUser;

  // url activa, se actualiza en cada navegación
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  // label del item o subitem cuyo routerLink coincide con la ruta activa
  public readonly currentPageTitle = computed(() => {
    const url = this.currentUrl();
    const currentRoute = this.getCurrentRoute();
    const routeTitle = currentRoute?.title;
    const menuTitle = this.findMenuTitle(url);
    const moduleTitle = this.findModuleTitle(currentRoute);
    const baseTitle = menuTitle ?? routeTitle ?? 'Inicio';
    const contextTitle = this.getContextualTitle(url, baseTitle);

    if (contextTitle) {
      return contextTitle;
    }

    if (routeTitle && moduleTitle) {
      return moduleTitle ? `${routeTitle} ${moduleTitle}` : routeTitle;
    }

    return baseTitle;
  });

  private getContextualTitle(url: string, fallbackTitle: string): string | undefined {
    const keys = [ACTIVE_NAVIGATION_CONTEXT_KEY, SUB_MODULE_NAVIGATION_CONTEXT_KEY];

    for (const key of keys) {
      const context = this.browserStorageService.getItem<NavigationContext>(key);

      if (!context?.name || !context.scope || !url.startsWith(context.scope)) continue;

      const label = context.entityLabel?.trim();
      const prefix = label ? `${fallbackTitle} ${label}` : fallbackTitle;
      return `${prefix}: ${context.name}`;
    }
    return undefined;
  }

  // imagen del logo de la compañía según el tema actual (oscuro o claro)
  public readonly companyLogo = computed(() =>
    this.themeService.isDark()
      ? '/images/logos/dark/company-short.png'
      : '/images/logos/light/company-short.png',
  );

  // iniciales del usuario a partir de su nombre completo
  public readonly userDisplayName = computed(() => {
    const currentUser = this.user();
    const candidateName = [currentUser?.name, currentUser?.userName, currentUser?.email]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim())[0];

    return candidateName ?? 'Usuario';
  });

  // Método para obtener las iniciales del usuario a partir de su nombre completo
  public readonly userInitials = computed(() => {
    const displayName = this.userDisplayName();
    const words = displayName.split(/\s+/).filter((value): value is string => value.length > 0);

    if (words.length === 0) return 'U';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  });

  // Constructor para inicializar la señal isMobile y agregar un listener para cambios en el tamaño de la ventana
  public constructor() {
    this.setupMobileListener();
  }

  // Configurar un listener para detectar cambios en el tamaño de la ventana y actualizar la señal isMobile
  private setupMobileListener(): void {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia('(max-width: 1023px)');
    this.isMobile.set(mql.matches);
    mql.addEventListener('change', (e) => {
      this.isMobile.set(e.matches);
      this.open = !e.matches;
    });
  }

  private getCurrentRoute(): ActivatedRouteSnapshot | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route;
  }

  private findModuleTitle(route: ActivatedRouteSnapshot | null): string | undefined {
    const moduleName = route?.paramMap.get('name');
    if (!moduleName) return undefined;

    for (const group of this.navGroups()) {
      for (const item of group.items) {
        if (item.routerLink?.endsWith(`/${moduleName}`)) {
          return item.label;
        }

        const subItem = item.subItems?.find((sub) => sub.routerLink?.endsWith(`/${moduleName}`));
        if (subItem) return subItem.label;
      }
    }

    return undefined;
  }

  private findMenuTitle(url: string): string | undefined {
    for (const group of this.navGroups()) {
      for (const item of group.items) {
        if (item.routerLink === url) return item.label;

        const subItem = item.subItems?.find((sub) => sub.routerLink === url);
        if (subItem) return subItem.label;
      }
    }

    return undefined;
  }

  public readonly navGroups = computed<NavGroup[]>(() => this.getVisibleNavGroups(NAV_GROUPS));

  private getVisibleNavGroups(groups: readonly NavGroup[]): NavGroup[] {
    return groups
      .map((group) => {
        const items = group.items
          .map((item) => this.filterNavItem(item))
          .filter((item): item is NavItem => item !== null);

        return {
          ...group,
          items,
        };
      })
      .filter((group) => group.items.length > 0);
  }

  private filterNavItem(item: NavItem): NavItem | null {
    if (item.visible === false) return null;
    if (!this.canAccess(item.requiredPermissions, item.permissionMode)) return null;

    const visibleSubItems = item.subItems
      ?.filter((subItem) => subItem.visible !== false)
      .filter((subItem) => this.canAccess(subItem.requiredPermissions, subItem.permissionMode));

    if (item.subItems && (!visibleSubItems || visibleSubItems.length === 0) && !item.routerLink) {
      return null;
    }

    const normalizedItem: NavItem = {
      ...item,
      ...(visibleSubItems ? { subItems: visibleSubItems } : {}),
    };

    return normalizedItem;
  }

  private canAccess(
    requiredPermissions: readonly Permission[] | undefined,
    permissionMode: PermissionCheckMode | undefined,
  ): boolean {
    return this.authorizationService.can(requiredPermissions, permissionMode ?? 'all');
  }

  public get userItems(): MenuItem[] {
    return [
      {
        label: this.userDisplayName(),
        items: [
          { label: 'Configuración', icon: 'cog', spin: true },
          { label: 'Notificaciones', icon: 'bell' },
          { separator: true },
          {
            label: 'Cerrar sesión',
            icon: 'sign-out',
            command: () => {
              this.authService.logout();
            },
          },
        ],
      },
    ];
  }

  public companies: Company[] = COMPANIES;

  public activeCompany = this.companies[0];

  public companyItems: MenuItem[] = [
    {
      label: 'Propietarios',
      items: [
        ...this.companies.map((c: Company) => ({
          label: c.name,
          logo: c.logo,
          color: c.color,
          active: this.activeCompany.name === c.name,
          command: () => {
            this.activeCompany = c;
            this.updateCompanyActive();
          },
        })),
        { separator: true },
        { label: 'Agregar Propietario', icon: 'plus' },
      ],
    },
  ];

  // Método para verificar si un NavItem tiene subItems activos
  public hasActiveSub(item: NavItem): boolean {
    const url = this.currentUrl();
    return !!item.subItems?.some((subItem: NavSubItem) => subItem.routerLink === url);
  }

  // Método para verificar si un NavItem (sin subItems) está activo según la ruta actual
  public isItemActive(item: NavItem): boolean {
    return !item.subItems && item.routerLink === this.currentUrl();
  }

  // Método para verificar si un subitem está activo según la ruta actual
  public isSubActive(sub: { routerLink?: string }): boolean {
    return sub.routerLink === this.currentUrl();
  }

  // Método para manejar el clic en el trigger del sidebar
  public onSidebarTriggerClick(): void {
    this.openOnHover = !this.openOnHover;
    this.backdrop = !this.backdrop;
  }

  // Método para actualizar el estado activo de los items de la compañía
  public updateCompanyActive(): void {
    const items = this.companyItems[0]?.items;
    if (!items) return;

    items.forEach((item: CustomMenuItem) => {
      if (item.logo) {
        item.active = this.activeCompany.name === item.label;
      }
    });
  }

  // Método limpiar el contexto de navegación
  public clearKeys(): void {
    this.browserStorageService.clearScope();
  }
}
